import { useMemo, useState } from 'react'
import { componentMeta, previews } from './components'

const propMap: Record<string, string[]> = {
  'aurora-orb': ['energy', 'scale', 'bloom'],
  'gravity-field': ['gravity', 'friction', 'fieldSize'],
  'prism-stack': ['depth', 'spread', 'transition'],
  'liquid-timeline': ['amplitude', 'flow', 'fade'],
  'sound-dial': ['intensity', 'pulse', 'glow'],
  'constellation-consent': ['nodeSize', 'connection', 'glow'],
  'focus-dock': ['magnitude', 'radius', 'delay'],
  'morph-grid': ['density', 'roundness', 'motion'],
  'halo-cursor': ['radius', 'attraction', 'trail'],
  horizon: ['horizon', 'drift', 'atmosphere'],
  'tectonic-terrain': ['confidence', 'relief', 'fog'],
  'trace-explorer': ['precision', 'context', 'fade'],
  'decision-ledger': ['speedWeight', 'confidence', 'tolerance'],
  'branch-map': ['branchDepth', 'pace', 'contrast'],
  'provenance-thread': ['depth', 'recency', 'linkStrength'],
  'signal-lattice': ['cellSize', 'signalGain', 'noise'],
  'tempo-queue': ['urgency', 'spacing', 'pulse'],
  'spectrum-compare': ['emphasis', 'difference', 'detail'],
  'context-rail': ['railWidth', 'noteDensity', 'focusFade'],
  'phase-gate': ['threshold', 'strictness', 'signalDecay'],
  'proof-spine': ['evidenceDepth', 'signalStrength', 'proofFade'],
  'offer-composer': ['teamSize', 'guidance', 'flexibility'],
  'capacity-weave': ['window', 'capacity', 'buffer'],
  'commitment-signal': ['responseTime', 'assurance', 'warmth'],
  'service-blueprint': ['stages', 'handoffOpacity', 'detail'],
  'scope-lens': ['depth', 'precision', 'boundaryStrength'],
  'return-path': ['memory', 'choiceCount', 'gentleness'],
  'partner-orbit': ['orbitSpread', 'similarity', 'motion'],
  'momentum-ledger': ['momentum', 'learning', 'riskVisibility'],
  'launch-relay': ['accessWave', 'anticipation', 'invitePace'],
  'handshake-board': ['agreement', 'tension', 'detail'],
  'memory-garden': ['growth', 'pruning', 'seedSize'],
  'presence-prism': ['focusRadius', 'overlap', 'motion'],
  'critique-compass': ['specificity', 'candor', 'care'],
  'drift-detector': ['sensitivity', 'history', 'tolerance'],
  'signal-shelf': ['capacity', 'urgency', 'fade'],
  'pattern-excavator': ['sampleSize', 'confidence', 'novelty'],
  'semantic-mosaic': ['connectionDepth', 'cluster', 'noise'],
  'attention-budget': ['availableFocus', 'buffer', 'interruptions'],
  'feedback-foundry': ['themeThreshold', 'grouping', 'sourceDetail'],
  'chapter-loom': ['chapterCount', 'weaveDensity', 'transition'],
  'tempo-reader': ['pace', 'measure', 'contrast'],
  'viewport-treaty': ['viewport', 'reflow', 'density'],
  'context-skin': ['intensity', 'warmth', 'shiftSpeed'],
  'wayfinding-ribbon': ['ribbonDepth', 'landmarkContrast', 'trailFade'],
  'narrative-switchboard': ['perspectiveDepth', 'contrast', 'transition'],
  'field-notes': ['noteDepth', 'sourceOpacity', 'margin'],
  'responsive-echo': ['continuity', 'adaptation', 'reduction'],
  'handoff-horizon': ['implementationConfidence', 'specDepth', 'validation'],
  'system-atlas': ['systemScale', 'connectionDensity', 'focus'],
}

export function App() {
  const [selected, setSelected] = useState('aurora-orb')
  const meta = componentMeta.find((item) => item.id === selected)!
  const [controls, setControls] = useState(() => Object.fromEntries(componentMeta.map((item) => [item.id, item.controls.map((control) => control.value)])))
  const Preview = previews[selected]
  const previewProps = useMemo(() => Object.fromEntries(propMap[selected].map((key, index) => [key, controls[selected][index]])), [controls, selected])
  const current = controls[selected]

  return <main>
    <aside className="catalog-rail">
      <a className="brand" href="#top"><i /> Kinetic<br />Systems Lab</a>
      <nav aria-label="Component catalogue">{componentMeta.map((item) => <button key={item.id} onClick={() => setSelected(item.id)} className={selected === item.id ? 'is-active' : ''}><span>{item.number}</span><em>{item.name}</em></button>)}</nav>
      <div className="rail-foot"><span>Batch 05 / 05</span><strong>50 / 50 shipped</strong></div>
    </aside>
    <section className="workspace" id="top">
      <header className="workspace-header"><span>Living interface collection</span><p>Use the left rail to inspect each component. Controls alter the live preview.</p><div className="header-state"><i /> Local lab</div></header>
      <div className="component-layout">
        <section className="preview-shell" aria-label={`${meta.name} live preview`}><div className="preview-topline"><span>{meta.category}</span><span>Live / configurable</span></div><Preview {...previewProps} /></section>
        <aside className="inspector">
          <div className="component-number">Component {meta.number}</div>
          <h1>{meta.name}</h1><p className="description">{meta.description}</p>
          <div className="tag-list">{meta.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
          <section className="control-section"><h2>Controls</h2>{meta.controls.map((control, index) => <label className="control" key={control.label}><span>{control.label}<b>{current[index]}{control.unit}</b></span><input type="range" min={control.min} max={control.max} value={current[index]} onChange={(event) => setControls((all) => ({ ...all, [selected]: all[selected].map((value, valueIndex) => valueIndex === index ? Number(event.target.value) : value) }))} /></label>)}</section>
          <section className="a11y-note"><h2>Access note</h2><p>{meta.accessibility}</p></section>
          <button className="copy-action" onClick={() => navigator.clipboard?.writeText(`<${meta.id} />`)}>Copy component reference</button>
        </aside>
      </div>
      <section className="catalogue-summary"><span>Batch 05 theme</span><h2>Interfaces that adapt without losing the thread.</h2><p>Ten systems for immersive stories, reading, reflow, situational atmosphere, orientation, perspective, field evidence, device continuity, delivery, and system thinking.</p></section>
    </section>
  </main>
}
